import { Injectable } from '@angular/core'

const DB_NAME = 'caracterra-offline'
const DB_VERSION = 1

const STORES = {
  missions: 'missions',
  samples: 'samples',
  weighings: 'weighings',
  photos: 'photos',
  syncQueue: 'sync_queue',
  meta: 'meta'
}

@Injectable({
  providedIn: 'root'
})
export class OfflineCacheService {
  private db: IDBDatabase

  init(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve()
        return
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result

        // missions store
        if (!db.objectStoreNames.contains(STORES.missions)) {
          db.createObjectStore(STORES.missions, { keyPath: 'id' })
        }

        // samples store
        if (!db.objectStoreNames.contains(STORES.samples)) {
          const sampleStore = db.createObjectStore(STORES.samples, { keyPath: 'id' })
          sampleStore.createIndex('missionId', 'mission.id', { unique: false })
          sampleStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        // weighings store
        if (!db.objectStoreNames.contains(STORES.weighings)) {
          const weighingStore = db.createObjectStore(STORES.weighings, { keyPath: 'id' })
          weighingStore.createIndex('sampleId', 'sampleId', { unique: false })
        }

        // photos store - composite key [entityType, entityId, fieldName]
        if (!db.objectStoreNames.contains(STORES.photos)) {
          db.createObjectStore(STORES.photos, { keyPath: ['entityType', 'entityId', 'fieldName'] })
        }

        // sync_queue store - auto-increment
        if (!db.objectStoreNames.contains(STORES.syncQueue)) {
          const syncStore = db.createObjectStore(STORES.syncQueue, { keyPath: 'id', autoIncrement: true })
          syncStore.createIndex('status', 'status', { unique: false })
          syncStore.createIndex('createdAt', 'createdAt', { unique: false })
        }

        // meta store
        if (!db.objectStoreNames.contains(STORES.meta)) {
          db.createObjectStore(STORES.meta, { keyPath: 'key' })
        }
      }

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result
        resolve()
      }

      request.onerror = (event) => {
        reject((event.target as IDBOpenDBRequest).error)
      }
    })
  }

  put(storeName: string, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readwrite')
      const store = tx.objectStore(storeName)
      store.put(data)

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  }

  get<T>(storeName: string, key: any): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readonly')
      const store = tx.objectStore(storeName)
      const request = store.get(key)

      request.onsuccess = () => resolve(request.result as T)
      request.onerror = () => reject(request.error)
    })
  }

  getAll<T>(storeName: string, indexName?: string, range?: IDBKeyRange): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readonly')
      const store = tx.objectStore(storeName)
      const source = indexName ? store.index(indexName) : store
      const request = range ? source.getAll(range) : source.getAll()

      request.onsuccess = () => resolve(request.result as T[])
      request.onerror = () => reject(request.error)
    })
  }

  delete(storeName: string, key: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readwrite')
      const store = tx.objectStore(storeName)
      store.delete(key)

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  }

  clear(storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readwrite')
      const store = tx.objectStore(storeName)
      store.clear()

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  }

  putPhoto(entityType: string, entityId: number | string, fieldName: string, blob: Blob, mimeType?: string): Promise<void> {
    return this.put(STORES.photos, {
      entityType,
      entityId,
      fieldName,
      blob,
      mimeType: mimeType || blob.type
    })
  }

  getPhoto(entityType: string, entityId: number | string, fieldName: string): Promise<Blob> {
    return this.get<{ blob: Blob }>(STORES.photos, [entityType, entityId, fieldName])
      .then(record => record ? record.blob : null)
  }

  async cleanupOldSamples(maxAgeDays: number): Promise<void> {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - maxAgeDays)
    const cutoffStr = cutoff.toISOString()

    const samples = await this.getAll<any>(STORES.samples)
    for (const sample of samples) {
      // Use the sample's actual creation date, not the cache timestamp
      const sampleDate = sample.createdAt || sample.receiptDate
      if (sampleDate && sampleDate < cutoffStr) {
        // Delete associated weighings
        const weighings = await this.getAll<any>(STORES.weighings, 'sampleId', IDBKeyRange.only(sample.id))
        for (const w of weighings) {
          await this.delete(STORES.weighings, w.id)
          // Delete weighing photos
          await this.deletePhotosForEntity('weighing', w.id).catch(() => {})
        }

        // Delete sample photos
        await this.deletePhotosForEntity('sample', sample.id).catch(() => {})

        // Delete sample
        await this.delete(STORES.samples, sample.id)
      }
    }
  }

  private async deletePhotosForEntity(entityType: string, entityId: number | string): Promise<void> {
    const tx = this.db.transaction(STORES.photos, 'readwrite')
    const store = tx.objectStore(STORES.photos)
    const request = store.openCursor()

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result
        if (cursor) {
          const record = cursor.value
          if (record.entityType === entityType && String(record.entityId) === String(entityId)) {
            cursor.delete()
          }
          cursor.continue()
        } else {
          resolve()
        }
      }
      request.onerror = () => reject(request.error)
    })
  }
}
